import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { AccountSession, AccountType, AllAccountsResponse, LoginRequest, UserInfo, AccountItemsResponse, ListPlayerRequest, ListPlayerResponse, QuickSellRequest, QuickSellResponse } from './account.type';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private apiUrl = environment.apiBaseURL + '/account-session';

  readonly http = inject(HttpClient);

  private stockAccountsSubject = new BehaviorSubject<AccountSession[]>([]);
  stockAccounts$ = this.stockAccountsSubject.asObservable();

  private customerAccountsSubject = new BehaviorSubject<AccountSession[]>([]);
  customerAccounts$ = this.customerAccountsSubject.asObservable();

  private allAccountsSubject = new BehaviorSubject<AccountSession[]>([]);
  allAccounts$ = this.allAccountsSubject.asObservable();


  getAccounts(type?: AccountType): Observable<AllAccountsResponse> {
    let params = new HttpParams();
    if (type) {
      params = params.set('type', type);
    }
    return this.http.get<AllAccountsResponse>(`${this.apiUrl}/all`, { params }).pipe(
      tap(accounts => {
        if (type === 'stock') {
          this.stockAccountsSubject.next(accounts.sessions);
        } else if (type === 'customer') {
          this.customerAccountsSubject.next(accounts.sessions);
        } else {
          this.allAccountsSubject.next(accounts.sessions);
        }
      })
    );
  }

  getStockAccounts(): Observable<AllAccountsResponse> {
    return this.getAccounts('stock');
  }

  getCustomerAccounts(): Observable<AllAccountsResponse> {
    return this.getAccounts('customer');
  }

  /**
   * Get account sessions of every type (stock, customer, contract)
   */
  getAllAccounts(): Observable<AllAccountsResponse> {
    return this.getAccounts();
  }

  /**
   * Create login on a specific server
   */
  createLogin(request: LoginRequest): Observable<any> {
    const serverApiUrl = this.apiUrl;
    return this.http.post<any>(`${serverApiUrl}/create`, request);
  }

  /**
   * Logout account from its owner server
   */
  logout(sessionId: string): Observable<any> {
    const serverApiUrl = this.apiUrl;
    return this.http.delete<any>(`${serverApiUrl}/${sessionId}`);
  }

  /**
   * Ensure SID validation for an account on its owner server
   */
  ensureValidation(sessionId: string): Observable<any> {
    const serverApiUrl = this.apiUrl;
    return this.http.post<any>(`${serverApiUrl}/${sessionId}/ensure-validation`, {});
  }

  /**
   * Reload account on its owner server
   */
  reload(sessionId: string): Observable<any> {
    const serverApiUrl = this.apiUrl;
    return this.http.post<any>(`${serverApiUrl}/${sessionId}/reload`, {});
  }

  /**
   * Trigger a quick-sell-all-cards operation on the account's owner server.
   * Fire-and-forget: the response only confirms the job started, not that it finished.
   */
  quickSellAll(sessionId: string): Observable<any> {
    const serverApiUrl = this.apiUrl;
    return this.http.post<any>(`${serverApiUrl}/${sessionId}/quick-sell-all`, {});
  }

  /**
   * Clear expired trade pile items (quick-sell expired auctions) on the account's owner server.
   * Fire-and-forget: the response only confirms the job started, not that it finished.
   */
  clearExpiredItems(sessionId: string): Observable<any> {
    const serverApiUrl = this.apiUrl;
    return this.http.post<any>(`${serverApiUrl}/${sessionId}/clear-expired-items`, {});
  }

  /**
   * Rename account on its owner server
   */
  rename(sessionId: string, newName: string): Observable<any> {
    const serverApiUrl = this.apiUrl;
    return this.http.post<any>(`${serverApiUrl}/rename`, { session_id: sessionId, new_name: newName });
  }

  /**
   * Get user info from a specific server
   */
  getUserInfo(sessionId: string): Observable<any> {
    const serverApiUrl = this.apiUrl;
    const params = new HttpParams({ fromObject: { session_id: sessionId } });
    return this.http.get<UserInfo>(`${serverApiUrl}/info`, { params });
  }

  /**
   * Get screenshot from account's owner server
   */
  getScreenshot(sessionId: string, from?: 'credit' | 'home' | 'transfer' | 'current' | 'setting'): Observable<Blob> {
    const serverApiUrl = this.apiUrl;
    let paramsObj: any = {};
    if (from) {
      paramsObj.from = from;
    }
    const params = new HttpParams({ fromObject: paramsObj });
    return this.http.get(`${serverApiUrl}/${sessionId}/screenshot`, { params, responseType: 'blob' });
  }

  /**
   * Get account items (tradepile and unassigned)
   */
  getAccountItems(sid: string): Observable<AccountItemsResponse> {
    const serverApiUrl = this.apiUrl;
    const params = new HttpParams({ fromObject: { sid } });
    return this.http.get<AccountItemsResponse>(`${serverApiUrl}/items`, { params });
  }

  /**
   * List a player on the transfer market
   */
  listPlayer(sid: string, request: ListPlayerRequest): Observable<ListPlayerResponse> {
    const serverApiUrl = this.apiUrl;
    const params = new HttpParams({ fromObject: { sid } });
    return this.http.post<ListPlayerResponse>(`${serverApiUrl}/list-player`, request, { params });
  }

  /**
   * Quick sell a player on the transfer market
   */
  quickSellPlayer(sid: string, request: QuickSellRequest): Observable<QuickSellResponse> {
    const serverApiUrl = this.apiUrl;
    const params = new HttpParams({ fromObject: { sid } });
    return this.http.post<QuickSellResponse>(`${serverApiUrl}/quick-sell`, request, { params });
  }
}